"use client"
import { useState, useEffect } from "react"
import { Input } from "@/components/ui/input"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import SendFeeNotification from "./SendFeeNotification"
import { API_ENDPOINTS } from "../../../constants/config"
import axiosInstance from "../../../utils/axiosInstance"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"
import { getFullName, getStudentProfileUrl } from "../../../utils/urlUtils"

const calculateDisplayTotal = (fee) => {
  if (fee?.totalPayable != null) return fee.totalPayable
  return fee?.totalAmount ?? 0
}

function StudentFeesContent({ classes }) {
  const [students, setStudents] = useState([])
  const [allStudents, setAllStudents] = useState([]) // Store all students for filtering
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true) // For initial data fetch
  const [studentFilter, setStudentFilter] = useState("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [studentClass, setStudentClass] = useState("all")
  const [paymentMode, setPaymentMode] = useState("all")
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingStatusChange, setPendingStatusChange] = useState(null) // { feeId, newStatus, currentStatus, fee, student }
  const [showWaiveDialog, setShowWaiveDialog] = useState(false)
  const [pendingWaiveChange, setPendingWaiveChange] = useState(null) // { feeId, waive, fee, student }
  const [waiveReason, setWaiveReason] = useState("")
  const [waiveLoading, setWaiveLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const navigate = useNavigate()

  // Fetch all students data - OPTIMIZED: Single API call instead of N+1 queries
  useEffect(() => {
    const fetchStudents = async () => {
      setInitialLoading(true)
      try {
        const token = localStorage.getItem("token")
        
        const response = await axiosInstance.get(
          API_ENDPOINTS.feesStudentsAll,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        )
        const studentsWithFees = response.data.data.students || []

        setAllStudents(studentsWithFees)
        setStudents(studentsWithFees)
      } catch (err) {
        console.error("Error fetching students:", err)
        setError("Failed to load students. Please try again.")
      } finally {
        setInitialLoading(false)
      }
    }

    fetchStudents()
  }, [])

  // Apply filters whenever filter values change
  useEffect(() => {
    if (allStudents.length === 0) return

    setLoading(true)
    
    // Apply filters
    let filteredStudents = [...allStudents]

    // Filter by class
    if (studentClass !== "all") {
      filteredStudents = filteredStudents.filter((student) => {
        // Extract just the number from the class name (e.g., "Class 1" -> "1")
        const studentClassNumber = student.className.replace("Class ", "")
        return studentClassNumber === studentClass
      })
    }

    // Filter by status - now filters both students AND their fees
    if (studentFilter !== "all") {
      filteredStudents = filteredStudents.map((student) => {
        // Filter the fees for this student based on status
        const filteredFees = student.fees.filter((fee) => {
          switch (studentFilter) {
            case "pending":
              return fee.status === "pending"
            case "pending-approval":
              return fee.status === "pending-approval"
            case "paid":
              return ["paid", "success", "completed"].includes(fee.status)
            default:
              return true
          }
        })
        
        // Return student with filtered fees
        return {
          ...student,
          fees: filteredFees
        }
      }).filter((student) => student.fees.length > 0) // Only show students who have fees matching the filter
    }

    // Filter by payment mode
    if (paymentMode !== "all") {
      filteredStudents = filteredStudents.map((student) => {
        // Filter the fees for this student based on payment mode
        const filteredFees = student.fees.filter((fee) => fee.paymentMode === paymentMode)
        
        // Return student with filtered fees
        return {
          ...student,
          fees: filteredFees
        }
      }).filter((student) => student.fees.length > 0) // Only show students who have fees matching the filter
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim()
      filteredStudents = filteredStudents.filter((student) => {
        const fullName = getFullName(student).toLowerCase()
        return fullName.includes(query)
      })
    }

    setStudents(filteredStudents)
    setCurrentPage(1) // Reset to first page when filters change
    setLoading(false)
  }, [studentFilter, studentClass, paymentMode, searchQuery, allStudents])

  const handleStatusChange = (feeId, status) => {
    if (!feeId) {
      setError("No fee record found for this student.")
      return
    }

    // Find the fee and associated student
    let currentFee = null
    let associatedStudent = null
    
    for (const student of allStudents) {
      const fee = student.fees.find((f) => f._id === feeId)
      if (fee) {
        currentFee = fee
        associatedStudent = student
        break
      }
    }

    if (!currentFee) {
      setError("Fee record not found.")
      return
    }

    // Prevent changing from paid/success to pending
    const isPaidStatus = currentFee.status === "success" || currentFee.status === "paid" || currentFee.isPaid === true
    const isChangingToPending = status === "pending" || status === "pending-approval"

    if (isPaidStatus && isChangingToPending) {
      toast.error("Cannot change status from paid to pending. Once a fee is marked as paid, it cannot be reverted.")
      return
    }

    // Show confirmation dialog with fee and student details
    setPendingStatusChange({
      feeId,
      newStatus: status,
      currentStatus: currentFee.status,
      fee: currentFee,
      student: associatedStudent
    })
    setShowConfirmDialog(true)
  }

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return

    const { feeId, newStatus } = pendingStatusChange

    try {
      setLoading(true)
      const response = await axiosInstance.patch(
        API_ENDPOINTS.feesStudentStatus(feeId),
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      )

      if (response.data.status === "success") {
        // Update the specific fee in both arrays
        const updateFeeInArray = (studentsArray) =>
          studentsArray.map((student) => ({
            ...student,
            fees: student.fees.map((fee) =>
              fee._id === feeId
                ? {
                    ...fee,
                    status: newStatus,
                    isPaid: newStatus === "success" || newStatus === "paid",
                    paidDate: newStatus === "success" || newStatus === "paid" ? new Date().toISOString() : fee.paidDate,
                  }
                : fee,
            ),
          }))

        setAllStudents(updateFeeInArray)
        setStudents(updateFeeInArray)

        toast.success("Status updated successfully")
        setShowConfirmDialog(false)
        setPendingStatusChange(null)
      }
    } catch (err) {
      console.error("Error updating status:", err)
      setError(err.response?.data?.message || "Failed to update status. Please try again.")
      toast.error(err.response?.data?.message || "Failed to update status")
      setShowConfirmDialog(false)
      setPendingStatusChange(null)
    } finally {
      setLoading(false)
    }
  }

  const handleWaiveLateFee = (feeId, waive) => {
    let currentFee = null
    let associatedStudent = null

    for (const student of allStudents) {
      const fee = student.fees.find((f) => f._id === feeId)
      if (fee) {
        currentFee = fee
        associatedStudent = student
        break
      }
    }

    if (!currentFee) {
      toast.error("Fee record not found.")
      return
    }

    setPendingWaiveChange({ feeId, waive, fee: currentFee, student: associatedStudent })
    setWaiveReason("")
    setShowWaiveDialog(true)
  }

  const confirmWaiveLateFee = async () => {
    if (!pendingWaiveChange) return

    const { feeId, waive } = pendingWaiveChange

    try {
      setWaiveLoading(true)
      const response = await axiosInstance.patch(
        API_ENDPOINTS.feesWaiveLateFee(feeId),
        { waive, ...(waive && waiveReason.trim() ? { reason: waiveReason.trim() } : {}) },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      )

      if (response.data.status === "success") {
        const { studentFee, lateFeeDetails, platformFee, totalPayable } = response.data.data

        const updateFeeInArray = (studentsArray) =>
          studentsArray.map((student) => ({
            ...student,
            fees: student.fees.map((fee) =>
              fee._id === feeId
                ? {
                    ...fee,
                    lateFeeWaived: studentFee.lateFeeWaived,
                    lateFeeWaiverReason: studentFee.lateFeeWaiverReason,
                    lateFeeDetails,
                    platformFee,
                    totalPayable,
                  }
                : fee,
            ),
          }))

        setAllStudents(updateFeeInArray)
        setStudents(updateFeeInArray)

        toast.success(response.data.message || (waive ? "Late fee waived successfully" : "Late fee waiver removed"))
        setShowWaiveDialog(false)
        setPendingWaiveChange(null)
        setWaiveReason("")
      }
    } catch (err) {
      console.error("Error updating late fee waiver:", err)
      toast.error(err.response?.data?.message || "Failed to update late fee waiver")
    } finally {
      setWaiveLoading(false)
    }
  }

  const handleStudentClick = (student) => {
    navigate(getStudentProfileUrl(student))
  }

  // Get all fee rows (flattened from students)
  const getAllFeeRows = () => {
    return students.flatMap((student) =>
      student.fees.length > 0
        ? student.fees.map((fee, index) => ({
            student,
            fee,
            key: `${student._id}-${index}`,
          }))
        : [{ student, fee: null, key: student._id }]
    )
  }

  // Paginate fee rows
  const getPaginatedFeeRows = () => {
    const allRows = getAllFeeRows()
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return allRows.slice(startIndex, endIndex)
  }


  // Loading skeleton component
  const LoadingSkeleton = () => (
    <div className="space-y-3">
      {[...Array(5)].map((_, index) => (
        <div key={index} className="flex items-center space-x-4 p-4 border rounded-lg animate-pulse">
          <div className="w-32 h-4 bg-gray-200 rounded"></div>
          <div className="w-20 h-4 bg-gray-200 rounded"></div>
          <div className="w-24 h-4 bg-gray-200 rounded"></div>
          <div className="w-24 h-4 bg-gray-200 rounded"></div>
          <div className="w-20 h-4 bg-gray-200 rounded"></div>
          <div className="w-24 h-4 bg-gray-200 rounded"></div>
          <div className="w-24 h-4 bg-gray-200 rounded"></div>
          <div className="w-32 h-4 bg-gray-200 rounded"></div>
          <div className="w-24 h-4 bg-gray-200 rounded"></div>
        </div>
      ))}
    </div>
  )

  const getStatusLabel = (status) => {
    switch (status) {
      case "success":
      case "paid":
        return "Paid"
      case "pending":
        return "Pending"
      case "pending-approval":
        return "Pending Approval"
      case "failed":
        return "Failed"
      default:
        return status
    }
  }

  if (initialLoading) {
    return (
      <div className="bg-white p-3 md:p-6 rounded-lg shadow space-y-3 md:space-y-4">
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center">
          <div className="w-full md:flex-1">
            <div className="w-full h-10 bg-gray-200 rounded animate-pulse"></div>
          </div>
          <div className="flex flex-row gap-2 w-full md:w-auto">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="w-32 h-10 bg-gray-200 rounded animate-pulse"></div>
            ))}
          </div>
        </div>
        <LoadingSkeleton />
      </div>
    )
  }

  return (
    <div className="bg-white p-3 md:p-6 rounded-lg shadow space-y-3 md:space-y-4">
      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Status Change</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>
                  Are you sure you want to change the fee status from{" "}
                  <strong>{pendingStatusChange ? getStatusLabel(pendingStatusChange.currentStatus) : ""}</strong> to{" "}
                  <strong>{pendingStatusChange ? getStatusLabel(pendingStatusChange.newStatus) : ""}</strong>?
                </p>
                
                {/* Show detailed fee information when changing to paid status */}
                {pendingStatusChange &&
                 (pendingStatusChange.currentStatus === "pending-approval" || pendingStatusChange.currentStatus === "pending") &&
                 (pendingStatusChange.newStatus === "success" || pendingStatusChange.newStatus === "paid") &&
                 pendingStatusChange.fee &&
                 pendingStatusChange.student && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
                    <h4 className="font-semibold text-sm text-gray-900">Payment Details for Verification</h4>
                    
                    {/* Student Information */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Student Name:</span>
                        <p className="font-medium">{getFullName(pendingStatusChange.student)}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Class:</span>
                        <p className="font-medium">{pendingStatusChange.student.className}</p>
                      </div>
                    </div>

                    {/* Payment Information */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Payment Mode:</span>
                        <p className="font-medium">{pendingStatusChange.fee.paymentMode || "N/A"}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Quarter:</span>
                        <p className="font-medium">{pendingStatusChange.fee.quarter?.toUpperCase() || "N/A"}</p>
                      </div>
                    </div>

                    {/* Transaction ID */}
                    <div className="text-sm">
                      <span className="text-gray-600">Transaction ID:</span>
                      <p className="font-medium font-mono text-xs bg-white px-2 py-1 rounded border mt-1">
                        {pendingStatusChange.fee.transactionId || "Not provided"}
                      </p>
                    </div>

                    {/* Razorpay Order ID */}
                    {pendingStatusChange.fee.razorpayOrderId && (
                      <div className="text-sm">
                        <span className="text-gray-600">Razorpay Order ID:</span>
                        <p className="font-medium font-mono text-xs bg-white px-2 py-1 rounded border mt-1">
                          {pendingStatusChange.fee.razorpayOrderId}
                        </p>
                      </div>
                    )}

                    {/* Razorpay Payment ID */}
                    {pendingStatusChange.fee.razorpayPaymentId && (
                      <div className="text-sm">
                        <span className="text-gray-600">Razorpay Payment ID:</span>
                        <p className="font-medium font-mono text-xs bg-white px-2 py-1 rounded border mt-1">
                          {pendingStatusChange.fee.razorpayPaymentId}
                        </p>
                      </div>
                    )}

                    {/* Payment History - Show most recent entry if available */}
                    {pendingStatusChange.fee.paymentHistory && pendingStatusChange.fee.paymentHistory.length > 0 && (
                      <div className="text-sm">
                        <span className="text-gray-600">Payment History:</span>
                        <div className="mt-1 bg-white p-2 rounded border text-xs space-y-1">
                          {pendingStatusChange.fee.paymentHistory.slice(-1).map((history, idx) => (
                            <div key={idx} className="space-y-1">
                              {history.transactionId && (
                                <div>
                                  <span className="text-gray-500">Transaction:</span>{" "}
                                  <span className="font-mono">{history.transactionId}</span>
                                </div>
                              )}
                              {history.remarks && (
                                <div>
                                  <span className="text-gray-500">Remarks:</span>{" "}
                                  <span>{history.remarks}</span>
                                </div>
                              )}
                              {history.paidDate && (
                                <div>
                                  <span className="text-gray-500">Date:</span>{" "}
                                  <span>{new Date(history.paidDate).toLocaleString()}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payment proof / Attachment (pending-approval) */}
                    {pendingStatusChange.fee.paymentHistory &&
                      pendingStatusChange.fee.paymentHistory.length > 0 &&
                      pendingStatusChange.fee.paymentHistory.slice(-1).some((h) => h.paymentProofUrl) && (
                      <div className="text-sm">
                        <span className="text-gray-600">Payment proof / Attachment:</span>
                        <div className="mt-1 flex flex-wrap gap-2">
                          {pendingStatusChange.fee.paymentHistory
                            .filter((h) => h.paymentProofUrl)
                            .map((history, idx) => (
                              <a
                                key={idx}
                                href={history.paymentProofUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                              >
                                View attachment
                              </a>
                            ))}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {pendingStatusChange.fee.paymentHistory
                            .filter((h) => h.paymentProofUrl)
                            .map((history, idx) => {
                              const url = history.paymentProofUrl
                              const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url) || url.includes("image")
                              return (
                                <div key={idx} className="border rounded overflow-hidden bg-white">
                                  {isImage ? (
                                    <img
                                      src={url}
                                      alt="Payment proof"
                                      className="max-h-40 object-contain"
                                    />
                                  ) : (
                                    <a href={url} target="_blank" rel="noopener noreferrer" className="block p-2 text-blue-600">
                                      Open proof
                                    </a>
                                  )}
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )}

                    {/* Amount Details */}
                    <div className="border-t pt-3 mt-3">
                      <span className="text-gray-600 text-sm">Amount Details:</span>
                      <div className="mt-2 space-y-1 text-sm">
                        {pendingStatusChange.fee.feeBreakup?.map((item, idx) => (
                          <div key={idx} className="flex justify-between">
                            <span className="text-gray-700">{item.feeName}</span>
                            <span className="font-medium">₹{item.amount?.toLocaleString()}</span>
                          </div>
                        ))}
                        {pendingStatusChange.fee.lateFeeDetails && pendingStatusChange.fee.lateFeeDetails.lateFeeAmount > 0 && (
                          <div className="flex justify-between text-red-600">
                            <span>Late Fee</span>
                            <span className="font-medium">₹{pendingStatusChange.fee.lateFeeDetails.lateFeeAmount?.toLocaleString()}</span>
                          </div>
                        )}
                        {(pendingStatusChange.fee?.platformFee ?? 0) > 0 && (
                          <div className="flex justify-between text-gray-600">
                            <span>Platform Fee</span>
                            <span className="font-medium">₹{(pendingStatusChange.fee.platformFee ?? 0).toLocaleString()}</span>
                          </div>
                        )}
                        <div className="flex justify-between border-t pt-2 font-semibold text-base">
                          <span>Total Amount</span>
                          <span>₹{calculateDisplayTotal(pendingStatusChange.fee)?.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Timestamp */}
                    {pendingStatusChange.fee.paidDate && (
                      <div className="text-sm text-gray-600">
                        <span>Payment Date:</span>
                        <p className="font-medium">{new Date(pendingStatusChange.fee.paidDate).toLocaleString()}</p>
                      </div>
                    )}

                    {/* Additional Notes */}
                    {pendingStatusChange.fee.notes && (
                      <div className="text-sm">
                        <span className="text-gray-600">Notes:</span>
                        <p className="mt-1 text-gray-700 bg-white p-2 rounded border">{pendingStatusChange.fee.notes}</p>
                      </div>
                    )}
                  </div>
                )}

                <p className="text-sm text-amber-600 font-medium">
                  ⚠️ Please verify all payment details above before confirming.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowConfirmDialog(false)
              setPendingStatusChange(null)
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmStatusChange} disabled={loading}>
              {loading ? "Updating..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Waive Late Fee Dialog */}
      <AlertDialog open={showWaiveDialog} onOpenChange={setShowWaiveDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingWaiveChange?.waive ? "Waive Late Fee" : "Remove Late Fee Waiver"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {pendingWaiveChange?.waive ? (
                  <p>
                    This will waive the late fee of{" "}
                    <strong>
                      ₹{pendingWaiveChange?.fee?.lateFeeDetails?.originalLateFeeAmount?.toLocaleString() ?? 0}
                    </strong>{" "}
                    for{" "}
                    <strong>{pendingWaiveChange?.student ? getFullName(pendingWaiveChange.student) : ""}</strong>{" "}
                    ({pendingWaiveChange?.fee?.quarter?.toUpperCase()}). The student will not be charged this late fee.
                  </p>
                ) : (
                  <p>
                    This will remove the existing late fee waiver for{" "}
                    <strong>{pendingWaiveChange?.student ? getFullName(pendingWaiveChange.student) : ""}</strong>{" "}
                    ({pendingWaiveChange?.fee?.quarter?.toUpperCase()}). The late fee will be charged again.
                  </p>
                )}

                {pendingWaiveChange?.waive && (
                  <div className="space-y-1">
                    <label htmlFor="waive-reason" className="text-sm text-gray-600">
                      Reason (optional)
                    </label>
                    <Textarea
                      id="waive-reason"
                      value={waiveReason}
                      onChange={(e) => setWaiveReason(e.target.value)}
                      placeholder="e.g. Family hardship, payment delayed due to system issue..."
                      className="text-sm"
                    />
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setShowWaiveDialog(false)
                setPendingWaiveChange(null)
                setWaiveReason("")
              }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmWaiveLateFee} disabled={waiveLoading}>
              {waiveLoading ? "Saving..." : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center">
        <div className="w-full md:flex-1">
          <Input
            type="text"
            placeholder="Search students..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="text-sm md:text-base"
            disabled={loading}
          />
        </div>

        <div className="flex flex-row gap-2 w-full md:w-auto">
          <Select value={studentClass} onValueChange={setStudentClass} disabled={loading}>
            <SelectTrigger className="w-full text-xs md:text-sm">
              <SelectValue placeholder="Class Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs md:text-sm">
                All Classes
              </SelectItem>
              {classes.map((classNum) => (
                <SelectItem key={classNum} value={classNum} className="text-xs md:text-sm">
                  Class {classNum}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={studentFilter} onValueChange={setStudentFilter} disabled={loading}>
            <SelectTrigger className="w-full text-xs md:text-sm">
              <SelectValue placeholder="Fee Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs md:text-sm">
                All Status
              </SelectItem>
              <SelectItem value="pending" className="text-xs md:text-sm">
                Pending
              </SelectItem>
              <SelectItem value="pending-approval" className="text-xs md:text-sm">
                Pending for Approval
              </SelectItem>
              <SelectItem value="paid" className="text-xs md:text-sm">
                Paid
              </SelectItem>
            </SelectContent>
          </Select>

          <Select value={paymentMode} onValueChange={setPaymentMode} disabled={loading}>
            <SelectTrigger className="w-full text-xs md:text-sm">
              <SelectValue placeholder="Payment Mode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs md:text-sm">
                All Modes
              </SelectItem>
              {studentFilter !== "pending" && (
                <>
                  <SelectItem value="QR" className="text-xs md:text-sm">
                    QR
                  </SelectItem>
                  <SelectItem value="Cash" className="text-xs md:text-sm">
                    Cash
                  </SelectItem>
                  <SelectItem value="Gateway" className="text-xs md:text-sm">
                    Payment Gateway
                  </SelectItem>
                  <SelectItem value="Cheque" className="text-xs md:text-sm">
                    Cheque
                  </SelectItem>
                  <SelectItem value="bank-transfer" className="text-xs md:text-sm">
                    Bank Transfer
                  </SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Loading indicator for filters */}
      {loading && (
        <div className="flex items-center justify-center py-4">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="text-sm text-gray-600">Applying filters...</span>
          </div>
        </div>
      )}

      <div className="flex items-center mb-2 md:mb-4">
        <div className="flex items-center">
          {studentFilter === "pending" && (
            <SendFeeNotification
              students={students.filter((s) => !s.currentFee?.schoolFee?.isPaid)}
              onSendNotification={() => {
                const event = new Event("statusChanged")
                window.dispatchEvent(event)
              }}
            />
          )}
        </div>
      </div>

      <div className="overflow-x-auto -mx-3 md:mx-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-xs md:text-sm">Student Name</TableHead>
              <TableHead className="text-xs md:text-sm">Class</TableHead>
              <TableHead className="text-xs md:text-sm whitespace-nowrap">Academic Year</TableHead>
              <TableHead className="text-xs md:text-sm">Fee Amount</TableHead>
              <TableHead className="text-xs md:text-sm">Quarter</TableHead>
              <TableHead className="text-xs md:text-sm">Status</TableHead>
              <TableHead className="text-xs md:text-sm">Payment Mode</TableHead>
              <TableHead className="text-xs md:text-sm">Timestamp</TableHead>
              <TableHead className="text-xs md:text-sm">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-gray-500 text-xs md:text-sm py-8">
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span>Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : getAllFeeRows().length > 0 ? (
              getPaginatedFeeRows().map(({ student, fee, key }) =>
                fee ? (
                  <TableRow key={key}>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4">
                      <span
                        className="text-[#0263cc] hover:text-[#0263cc]/80 cursor-pointer font-medium"
                        onClick={() => handleStudentClick(student)}
                      >
                        {getFullName(student)}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4">{student.className}</TableCell>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4 whitespace-nowrap">
                      {student.academicYear}
                    </TableCell>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4">
                      ₹
                      {calculateDisplayTotal(fee)?.toLocaleString()}
                      {fee.feeBreakup && fee.feeBreakup.length > 0 && (
                        <div className="mt-1">
                          <details className="text-xs text-gray-500">
                            <summary className="cursor-pointer hover:text-gray-700">View Breakdown</summary>
                            <div className="mt-1 pl-2 space-y-1">
                              {fee.feeBreakup.map((item, idx) => (
                                <div key={idx} className="flex justify-between">
                                  <span>{item.feeName}</span>
                                  <span>₹{item.amount?.toLocaleString()}</span>
                                </div>
                              ))}
                              {fee.lateFeeDetails && (fee.lateFeeDetails.lateFeeAmount > 0 || (fee.lateFeeDetails.waived && fee.lateFeeDetails.originalLateFeeAmount > 0)) && (
                                <div className="flex flex-col gap-1">
                                  <div className={`flex justify-between ${fee.lateFeeDetails.waived ? "text-gray-400 line-through" : "text-red-500"}`}>
                                    <span>
                                      {fee.lateFeeDetails.matchedSlab?.slabType === "flat" ? (
                                        <>Late Fee (Flat Slab: ₹{fee.lateFeeDetails.matchedSlab.amount?.toLocaleString()})</>
                                      ) : fee.lateFeeDetails.matchedSlab?.slabType === "per_day" ? (
                                        <>Late Fee ({fee.lateFeeDetails.matchedSlab.daysApplied || fee.lateFeeDetails.daysLate} days @ ₹{fee.lateFeeDetails.matchedSlab.perDayAmount || fee.lateFeeDetails.perDayLateFee}/day)</>
                                      ) : (
                                        <>Late Fee ({fee.lateFeeDetails.daysLate} days @ ₹{fee.lateFeeDetails.perDayLateFee || 0}/day)</>
                                      )}
                                    </span>
                                    <span>₹{(fee.lateFeeDetails.waived ? fee.lateFeeDetails.originalLateFeeAmount : fee.lateFeeDetails.lateFeeAmount)?.toLocaleString()}</span>
                                  </div>
                                  {fee.lateFeeDetails.waived && (
                                    <div className="flex justify-between text-green-600 font-medium">
                                      <span>Late Fee Waived</span>
                                      <span>₹0</span>
                                    </div>
                                  )}
                                  <div className="text-xs text-gray-400">
                                    Due date was {new Date(fee.lateFeeDetails.dueDate).toLocaleDateString()}
                                    {fee.lateFeeDetails.matchedSlab?.slabType === "flat" && fee.lateFeeDetails.matchedSlab.startDate && fee.lateFeeDetails.matchedSlab.endDate && (
                                      <> • Slab: {new Date(fee.lateFeeDetails.matchedSlab.startDate).toLocaleDateString()} - {new Date(fee.lateFeeDetails.matchedSlab.endDate).toLocaleDateString()}</>
                                    )}
                                  </div>
                                  {fee.lateFeeWaiverReason && (
                                    <div className="text-xs text-gray-400">Reason: {fee.lateFeeWaiverReason}</div>
                                  )}
                                </div>
                              )}
                              {(fee?.platformFee ?? 0) > 0 && (
                                <div className="flex justify-between">
                                  <span>Platform Fee</span>
                                  <span>₹{(fee.platformFee ?? 0).toLocaleString()}</span>
                                </div>
                              )}
                              <div className="border-t border-gray-200 mt-2 pt-2 font-medium">
                                <div className="flex justify-between">
                                  <span>Total</span>
                                  <span>
                                    ₹
                                    {calculateDisplayTotal(fee)?.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </details>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4">
                      {fee.quarter?.toUpperCase() || "N/A"}
                    </TableCell>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4">
                      {(() => {
                        if (!fee) return <Badge variant="destructive">No Fees</Badge>

                        if (fee.status === "success" || fee.status === "paid") {
                          return <Badge variant="success">Paid</Badge>
                        }
                        if (fee.status === "pending") {
                          return <Badge variant="warning">Pending</Badge>
                        }
                        if (fee.status === "pending-approval") {
                          const proofUrl = fee.paymentHistory?.slice(-1)[0]?.paymentProofUrl
                          return (
                            <div className="flex flex-col gap-1">
                              <Badge variant="secondary">Pending Approval</Badge>
                              {proofUrl && (
                                <a
                                  href={proofUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-xs text-blue-600 hover:underline"
                                >
                                  View attachment
                                </a>
                              )}
                            </div>
                          )
                        }
                        if (fee.status === "failed") {
                          return <Badge variant="destructive">Failed</Badge>
                        }

                        return <Badge variant="secondary">Unknown Status</Badge>
                      })()}
                    </TableCell>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4">{fee.paymentMode || "N/A"}</TableCell>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4 whitespace-nowrap">
                      {fee.paidDate ? new Date(fee.paidDate).toLocaleString() : "N/A"}
                    </TableCell>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4">
                      <div className="flex flex-col gap-2">
                        <Select
                          onValueChange={(value) => handleStatusChange(fee._id, value)}
                          value={fee.status}
                          disabled={loading || fee.status === "success" || fee.status === "paid" || fee.isPaid === true}
                        >
                          <SelectTrigger className="w-32 h-8 text-xs">
                            <SelectValue placeholder="Edit Status" />
                          </SelectTrigger>
                          <SelectContent>
                            {(fee.status === "success" || fee.status === "paid" || fee.isPaid === true) ? (
                              <SelectItem value="success" className="text-xs">
                                Paid
                              </SelectItem>
                            ) : (
                              <>
                                <SelectItem value="pending" className="text-xs">
                                  Pending
                                </SelectItem>
                                <SelectItem value="pending-approval" className="text-xs">
                                  Pending Approval
                                </SelectItem>
                                <SelectItem value="success" className="text-xs">
                                  Paid
                                </SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>

                        {fee.status !== "success" &&
                          fee.status !== "paid" &&
                          fee.isPaid !== true &&
                          fee.lateFeeDetails &&
                          fee.lateFeeDetails.originalLateFeeAmount > 0 && (
                            <Button
                              variant="outline"
                              size="sm"
                              className={`h-8 w-32 text-xs ${fee.lateFeeDetails.waived ? "" : "text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"}`}
                              disabled={loading}
                              onClick={() => handleWaiveLateFee(fee._id, !fee.lateFeeDetails.waived)}
                            >
                              {fee.lateFeeDetails.waived ? "Remove Waiver" : "Waive Late Fee"}
                            </Button>
                          )}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={key}>
                    <TableCell className="text-xs md:text-sm py-2 md:py-4">{getFullName(student)}</TableCell>
                    <TableCell colSpan={8} className="text-center text-gray-500 text-xs md:text-sm">
                      No fees found
                    </TableCell>
                  </TableRow>
                )
              )
            ) : (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-gray-500 text-xs md:text-sm py-8">
                  {searchQuery || studentClass !== "all" || studentFilter !== "all" || paymentMode !== "all" 
                    ? "No students match the current filters" 
                    : "No students found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {getAllFeeRows().length > 0 && (
        <div className="p-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-sm text-gray-600">
            Showing{" "}
            {Math.min(
              (currentPage - 1) * itemsPerPage + 1,
              getAllFeeRows().length
            )}{" "}
            to{" "}
            {Math.min(
              currentPage * itemsPerPage,
              getAllFeeRows().length
            )}{" "}
            of {getAllFeeRows().length} {getAllFeeRows().length === 1 ? "fee" : "fees"}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={String(itemsPerPage)}
              onValueChange={(value) => {
                setItemsPerPage(Number(value))
                setCurrentPage(1)
              }}
            >
              <SelectTrigger className="w-[80px]">
                <SelectValue placeholder="10" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>

            <div className="flex items-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) => Math.max(prev - 1, 1))
                }
                disabled={currentPage === 1}
                className="h-8 px-2 rounded-r-none"
              >
                Previous
              </Button>

              <div className="flex items-center border-y border-x">
                {(() => {
                  const totalPages = Math.ceil(
                    getAllFeeRows().length / itemsPerPage
                  )
                  const maxButtons = 5 // Maximum number of page buttons to show
                  let startPage = Math.max(
                    1,
                    currentPage - Math.floor(maxButtons / 2)
                  )
                  const endPage = Math.min(
                    totalPages,
                    startPage + maxButtons - 1
                  )

                  // Adjust if we're near the end
                  if (endPage - startPage + 1 < maxButtons) {
                    startPage = Math.max(1, endPage - maxButtons + 1)
                  }

                  const pages = []

                  // Add first page and ellipsis if needed
                  if (startPage > 1) {
                    pages.push(
                      <Button
                        key="1"
                        variant={
                          currentPage === 1 ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() => setCurrentPage(1)}
                        className="h-8 w-8 p-0 rounded-none"
                      >
                        1
                      </Button>
                    )
                    if (startPage > 2) {
                      pages.push(
                        <Button
                          key="ellipsis1"
                          variant="ghost"
                          size="sm"
                          disabled
                          className="h-8 w-8 p-0 rounded-none"
                        >
                          ...
                        </Button>
                      )
                    }
                  }

                  // Add page buttons
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <Button
                        key={i}
                        variant={
                          currentPage === i ? "default" : "ghost"
                        }
                        size="sm"
                        onClick={() => setCurrentPage(i)}
                        className="h-8 w-8 p-0 rounded-none"
                      >
                        {i}
                      </Button>
                    )
                  }

                  // Add last page and ellipsis if needed
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <Button
                          key="ellipsis2"
                          variant="ghost"
                          size="sm"
                          disabled
                          className="h-8 w-8 p-0 rounded-none"
                        >
                          ...
                        </Button>
                      )
                    }
                    pages.push(
                      <Button
                        key={totalPages}
                        variant={
                          currentPage === totalPages
                            ? "default"
                            : "ghost"
                        }
                        size="sm"
                        onClick={() => setCurrentPage(totalPages)}
                        className="h-8 w-8 p-0 rounded-none"
                      >
                        {totalPages}
                      </Button>
                    )
                  }

                  return pages
                })()}
              </div>

              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setCurrentPage((prev) =>
                    Math.min(
                      prev + 1,
                      Math.ceil(getAllFeeRows().length / itemsPerPage)
                    )
                  )
                }
                disabled={
                  currentPage ===
                    Math.ceil(getAllFeeRows().length / itemsPerPage) ||
                  getAllFeeRows().length === 0
                }
                className="h-8 px-2 rounded-l-none"
              >
                Next
              </Button>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm">Go to:</span>
              <Input
                type="number"
                min="1"
                max={Math.max(
                  1,
                  Math.ceil(getAllFeeRows().length / itemsPerPage)
                )}
                value={currentPage}
                onChange={(e) => {
                  const value = Number.parseInt(e.target.value)
                  if (
                    !isNaN(value) &&
                    value >= 1 &&
                    value <=
                      Math.ceil(getAllFeeRows().length / itemsPerPage)
                  ) {
                    setCurrentPage(value)
                  }
                }}
                className="w-16 h-8"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default StudentFeesContent
